import{useEffect,useMemo,useRef,useState}from"react";
import{authApi,type WorkspaceAccess}from"../lib/authApi";
import{useAppI18n,type I18nKey}from"../lib/appI18n";

type SearchCategory="security"|"operations"|"workspace";
interface SearchItem{
 id:string;
 labelKey?:I18nKey;
 label?:string;
 description?:string;
 category:SearchCategory;
 href:string;
 aliases:string[];
}

const STATIC_ITEMS:SearchItem[]=[
 {id:"security-overview",labelKey:"nav.overview",category:"security",href:"/security",aliases:["overview","security","sicherheit","güvenlik","genel bakış"]},
 {id:"security-users",labelKey:"nav.users",category:"security",href:"/security?section=users",aliases:["users","people","benutzer","kullanıcı","kullanicilar"]},
 {id:"security-registrations",labelKey:"nav.registrations",category:"security",href:"/security?section=registrations",aliases:["registration","registrierung","kayıt","kayit","onboarding"]},
 {id:"security-access",labelKey:"nav.identityAccess",category:"security",href:"/security?section=access",aliases:["identity","access","iam","zugriff","identität","kimlik","erişim","erisim","yetki","role","rolle"]},
 {id:"security-requests",labelKey:"nav.requests",category:"security",href:"/security?section=requests",aliases:["request","approval","anfrage","genehmigung","istek","onay","jit"]},
 {id:"security-devices",labelKey:"nav.devices",category:"security",href:"/security?section=devices",aliases:["device","passkey","credential","gerät","cihaz","anahtar"]},
 {id:"security-sessions",labelKey:"nav.sessions",category:"security",href:"/security?section=sessions",aliases:["session","sessions","sitzung","sitzungen","oturum","oturumlar"]},
 {id:"security-vaults",labelKey:"nav.vaults",category:"security",href:"/security?section=vaults",aliases:["vault","vaults","tresor","kasa","kasalar"]},
 {id:"security-recovery",labelKey:"nav.recovery",category:"security",href:"/security?section=recovery",aliases:["recovery","wiederherstellung","kurtarma","hesap kurtarma"]},
 {id:"security-audit",labelKey:"nav.audit",category:"security",href:"/security?section=audit",aliases:["audit","log","events","prüfung","protokoll","denetim","olay"]},
 {id:"security-policies",labelKey:"nav.policies",category:"security",href:"/security?section=policies",aliases:["policy","policies","richtlinie","richtlinien","politika","politikalar"]},
 {id:"security-settings",labelKey:"nav.settings",category:"security",href:"/security?section=settings",aliases:["settings","configuration","einstellungen","ayar","ayarlar","konfigürasyon"]},
 {id:"operations-agent",labelKey:"nav.agentControl",category:"operations",href:"/operations/agent-control",aliases:["agent","agent control","ajan","ajan kontrol","agentensteuerung","cline"]},
 {id:"operations-provider",labelKey:"nav.providerMarket",category:"operations",href:"/operations/provider-market",aliases:["provider","provider market","model","models","sağlayıcı","saglayici","anbieter","modelle"]},
];

function normalize(value:string){
 return value.toLocaleLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g,"").trim();
}

export function GlobalSearch({shortcut=true}:{shortcut?:boolean}){
 const{t}=useAppI18n();
 const[open,setOpen]=useState(false);
 const[query,setQuery]=useState("");
 const[workspaces,setWorkspaces]=useState<WorkspaceAccess[]>([]);
 const inputRef=useRef<HTMLInputElement|null>(null);

 useEffect(()=>{
  let cancelled=false;
  void authApi.me()
   .then(me=>{if(!cancelled)setWorkspaces(me.authorization?.workspaces??[]);})
   .catch(()=>{});
  return()=>{cancelled=true;};
 },[]);

 useEffect(()=>{
  const show=()=>{
   setOpen(true);
   window.requestAnimationFrame(()=>inputRef.current?.focus());
  };
  const onCustom=()=>show();
  const onKey=(event:KeyboardEvent)=>{
   if(!shortcut)return;
   if(!(event.metaKey||event.ctrlKey)||event.key.toLowerCase()!=="k")return;
   const target=event.target as HTMLElement|null;
   if(target?.matches("input,textarea,select,[contenteditable='true']"))return;
   event.preventDefault();
   show();
  };
  window.addEventListener("knsaix:global-search",onCustom as EventListener);
  window.addEventListener("keydown",onKey);
  return()=>{
   window.removeEventListener("knsaix:global-search",onCustom as EventListener);
   window.removeEventListener("keydown",onKey);
  };
 },[shortcut]);

 useEffect(()=>{
  if(!open)return;
  const onEscape=(event:KeyboardEvent)=>{if(event.key==="Escape")setOpen(false);};
  window.addEventListener("keydown",onEscape);
  return()=>window.removeEventListener("keydown",onEscape);
 },[open]);

 const workspaceItems=useMemo<SearchItem[]>(()=>workspaces
  .filter(row=>!["SECURITY_CENTER","AGENT_CONTROL"].includes(row.key))
  .map(row=>({
   id:`workspace-${row.key}`,
   label:row.display_name,
   description:row.module_key,
   category:"workspace"as const,
   href:row.route,
   aliases:[row.key,row.module_key,row.display_name],
  })),[workspaces]);

 const visible=useMemo(()=>{
  const all=[...STATIC_ITEMS,...workspaceItems];
  const q=normalize(query);
  if(!q)return all.slice(0,14);
  return all.map(item=>{
   const label=item.label??(item.labelKey?t(item.labelKey):item.id);
   const hay=normalize([label,item.description??"",...item.aliases].join(" "));
   const score=hay===q?100:hay.startsWith(q)?60:hay.includes(q)?30:0;
   return{item,score};
  }).filter(x=>x.score>0).sort((a,b)=>b.score-a.score).slice(0,20).map(x=>x.item);
 },[query,t,workspaceItems]);

 const openItem=(item:SearchItem)=>{
  setOpen(false);
  window.location.assign(item.href);
 };

 return(
  <>
   <button
    type="button"
    className="v2-global-search-trigger"
    onClick={()=>{
     setOpen(true);
     window.requestAnimationFrame(()=>inputRef.current?.focus());
    }}
   >
    <span aria-hidden="true">⌕</span>
    <span>{t("search.trigger")}</span>
    {shortcut?<kbd>Ctrl K</kbd>:null}
   </button>

   {open?
    <div className="v2-search-backdrop" role="presentation" onMouseDown={()=>setOpen(false)}>
     <section
      className="v2-search-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={t("search.title")}
      onMouseDown={event=>event.stopPropagation()}
     >
      <div className="v2-search-input-row">
       <span aria-hidden="true">⌕</span>
       <input
        ref={inputRef}
        value={query}
        onChange={event=>setQuery(event.target.value)}
        placeholder={t("search.placeholder")}
        onKeyDown={event=>{
         if(event.key==="Enter"&&visible[0]){
          event.preventDefault();
          openItem(visible[0]);
         }
        }}
       />
       <button type="button" onClick={()=>setOpen(false)} aria-label="Close">Esc</button>
      </div>
      <p className="v2-search-hint">{t("search.hint")}</p>
      <div className="v2-search-results">
       {visible.map(item=>{
        const label=item.label??(item.labelKey?t(item.labelKey):item.id);
        const category=
         item.category==="security"?t("search.category.security"):
         item.category==="operations"?t("search.category.operations"):
         t("search.category.workspace");
        return(
         <button key={item.id} type="button" className="v2-search-result" onClick={()=>openItem(item)}>
          <span>
           <strong>{label}</strong>
           {item.description?<small>{item.description}</small>:null}
          </span>
          <em>{category}</em>
         </button>
        );
       })}
       {!visible.length?<div className="v2-search-empty">{t("search.empty")}</div>:null}
      </div>
     </section>
    </div>
   :null}
  </>
 );
}
